import classNames from "classnames";
import ons from "onsenui";
import React, { PropsWithChildren, ReactNode } from "react";
import { assertNotNull } from "../../utils/assertUndefinedable";
import { getCSSIOSSuffix, getDeviceType } from "../../utils/DeviceUtils";
import { MENU_ICON } from "../../utils/svgs_new";
import BackButton from "../BackButton/BackButton";
import { AppIcon } from "../Generic/AppIcon";
import { IsDefined } from "../Generic/IsDefined";
import SafeArea from "../Generic/SafeArea";
import useOverlayScaffold from "./useOverlayScaffold";

type BaseProps<$As extends React.ElementType> = {
  as?: $As;
  scrollRef?: React.RefObject<HTMLDivElement>;
  title?: ReactNode;
  extraTop?: ReactNode;
  extraHeader?: ReactNode;
  startActions?: ReactNode;
  endActions?: ReactNode;
  footer?: ReactNode;
  className?: string;
  overlayHeader?: boolean | { from: number };
  theme?: "light" | "dark";
  onClickMenu?: () => void;
  onClickGoBack?: () => void;
} & Omit<React.ComponentPropsWithoutRef<$As>, "title">;

interface ScaffoldMenuButtonProps {
  onClickMenu: () => void;
  onClickGoBack?: undefined;
}

interface ScaffoldBackButtonProps {
  onClickGoBack: () => void;
  onClickMenu?: undefined;
}

export type ScaffoldProps<$As extends React.ElementType> = BaseProps<$As> &
  (ScaffoldMenuButtonProps | ScaffoldBackButtonProps);

const Scaffold = <$As extends React.ElementType = "div">(
  props: PropsWithChildren<ScaffoldProps<$As>>
) => {
  const {
    as,
    title,
    extraTop,
    extraHeader,
    startActions,
    endActions,
    footer,
    className,
    overlayHeader,
    theme,
    onClickMenu,
    onClickGoBack,
    scrollRef: outerScrollRef,
    ...rest
  } = props;

  const headerRef = React.useRef<HTMLDivElement>(null);
  const innerScrollRef = React.useRef<HTMLDivElement>(null);
  const scrollRef = outerScrollRef ?? innerScrollRef;

  const startActionsWithIcon = React.useMemo(() => {
    return (
      <>
        {props.onClickGoBack && <Scaffold.HeaderMainAction onClickGoBack={props.onClickGoBack} />}
        {props.onClickMenu && <Scaffold.HeaderMainAction onClickMenu={props.onClickMenu} />}
        {props.startActions}
      </>
    );
  }, [props.onClickGoBack, props.onClickMenu, props.startActions]);

  const As = props.as ?? "div";

  return (
    <ScaffoldProvider>
      <As
        className={classNames("Scaffold", props.className)}
        data-overlay-header={Boolean(props.overlayHeader)}
        data-ios-suffix={getCSSIOSSuffix()}
        data-android={ons.platform.isAndroid()}
        data-ios={ons.platform.isIPhone()}
        data-user-agent={getDeviceType()}
        data-theme={props.theme}
        {...rest}
      >
        {props.extraTop}
        <Scaffold.Header overlay={overlayHeader} ref={headerRef}>
          <Scaffold.HeaderContent>
            {startActionsWithIcon}
            <Scaffold.HeaderTitle>{props.title}</Scaffold.HeaderTitle>
            <Scaffold.HeaderActions>{props.endActions}</Scaffold.HeaderActions>
          </Scaffold.HeaderContent>
          <IsDefined value={props.extraHeader}>
            {(extraHeader) => <Scaffold.HeaderExtra>{extraHeader}</Scaffold.HeaderExtra>}
          </IsDefined>
        </Scaffold.Header>
        <Scaffold.Body ref={scrollRef}>{props.children}</Scaffold.Body>
        <IsDefined value={props.footer}>
          {(footer) => (
            <SafeArea bottom={true}>
              <Scaffold.Footer>{footer}</Scaffold.Footer>
            </SafeArea>
          )}
        </IsDefined>
      </As>
    </ScaffoldProvider>
  );
};

const ScaffoldContext = React.createContext<{
  scrollRef: React.RefObject<HTMLDivElement>;
  headerRef: React.RefObject<HTMLDivElement>;
} | null>(null);

const ScaffoldProvider = ({ children }: { children: ReactNode }) => {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);

  return (
    <ScaffoldContext.Provider value={{ scrollRef, headerRef }}>{children}</ScaffoldContext.Provider>
  );
};

function useScaffold() {
  const context = React.useContext(ScaffoldContext);

  if (!context) {
    throw new Error("useScaffold must be used within a Scaffold.Root");
  }

  return context;
}

interface ScaffoldRootProps<$As extends React.ElementType>
  extends React.ComponentPropsWithoutRef<"div"> {
  as?: $As;
  theme?: "light" | "dark";
}

const ScaffoldRoot = <$As extends React.ElementType = "div">({
  children,
  as,
  theme,
  ...props
}: ScaffoldRootProps<$As>) => {
  const As = as ?? "div";

  return (
    <ScaffoldProvider>
      <As
        className={classNames("Scaffold", props.className)}
        data-ios-suffix={getCSSIOSSuffix()}
        data-android={ons.platform.isAndroid()}
        data-ios={ons.platform.isIPhone()}
        data-user-agent={getDeviceType()}
        data-theme={theme}
        {...props}
      >
        {children}
      </As>
    </ScaffoldProvider>
  );
};

interface ScaffoldHeaderProps extends React.ComponentPropsWithoutRef<"div"> {
  overlay?: boolean | { from: number };
}

const ScaffoldHeader = React.forwardRef<HTMLDivElement, ScaffoldHeaderProps>(
  ({ className, overlay, ...props }, imperativeRef) => {
    const { headerRef, scrollRef } = useScaffold();

    useOverlayScaffold({
      headerRef: headerRef,
      scrollRef: scrollRef,
      shouldOverlay: overlay ?? false,
    });

    React.useImperativeHandle(imperativeRef, () => assertNotNull(headerRef.current));

    return (
      <div
        data-overlay-header={Boolean(overlay)}
        className={classNames("__header", className)}
        ref={headerRef}
        {...props}
      />
    );
  }
);

const ScaffoldHeaderContent = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
  return <div className={classNames("__headerWrapper", className)} ref={ref} {...props} />;
});

type ScaffoldHeaderMainAction =
  | { onClickMenu: () => void; onClickGoBack?: undefined }
  | { onClickGoBack: () => void; onClickMenu?: undefined };

const ScaffoldHeaderMainAction = React.forwardRef<HTMLDivElement, ScaffoldHeaderMainAction>(
  (props, ref) => {
    if (props.onClickMenu) {
      return (
        <div className="__mainAction" onClick={props.onClickMenu} ref={ref}>
          <AppIcon icon={MENU_ICON} />
        </div>
      );
    }

    return (
      <div className="__mainAction" onClick={props.onClickGoBack} ref={ref}>
        <BackButton />
      </div>
    );
  }
);

const ScaffoldHeaderTitle = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div className={classNames("__title", className)} ref={ref} {...props} />;
  }
);

const ScaffoldHeaderActions = React.forwardRef<
  HTMLDivElement,
  React.ComponentPropsWithoutRef<"div">
>(({ className, ...props }, ref) => {
  return <div className={classNames("__actions", className)} ref={ref} {...props} />;
});

const ScaffoldHeaderExtra = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div className={classNames("__headerExtra", className)} ref={ref} {...props} />;
  }
);

const ScaffoldBody = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, imperativeRef) => {
    const { scrollRef } = useScaffold();

    React.useImperativeHandle(imperativeRef, () => assertNotNull(scrollRef.current));

    return <div className={classNames("__body", className)} ref={scrollRef} {...props} />;
  }
);

const ScaffoldFooter = React.forwardRef<HTMLDivElement, React.ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => {
    return <div className={classNames("__footer", className)} ref={ref} {...props} />;
  }
);

const ScaffoldContextAccessor = (props: {
  children: (context: ReturnType<typeof useScaffold>) => React.ReactElement;
}) => {
  const context = useScaffold();

  return props.children(context);
};

Scaffold.Root = ScaffoldRoot;
Scaffold.Header = ScaffoldHeader;
Scaffold.HeaderContent = ScaffoldHeaderContent;
Scaffold.HeaderMainAction = ScaffoldHeaderMainAction;
Scaffold.HeaderTitle = ScaffoldHeaderTitle;
Scaffold.HeaderActions = ScaffoldHeaderActions;
Scaffold.HeaderExtra = ScaffoldHeaderExtra;
Scaffold.Body = ScaffoldBody;
Scaffold.Footer = ScaffoldFooter;
Scaffold.Context = ScaffoldContextAccessor;

export default Scaffold;
